import React, { useEffect, useRef, useState } from 'react'
import algoliasearch from 'algoliasearch/lite'
import {
    InstantSearch,
    connectSearchBox,
    connectSortBy,
    connectMenu,
    connectHits,
    Configure,
    connectCurrentRefinements,
    ScrollTo,
} from 'react-instantsearch-dom'
import { gcgPointsToCashBack } from '../../utility'
import {
    sendProductClickToDataLayer,
    sendProductImpressionsToDatalayer,
} from '../../utils/dataLayer'
import unescape from 'unescape'
import generateMerchantSubtitle from '../../utils/merchantSubtitleGenerator'
import generateMerchantUrl from '../../utils/merchantUrlGenerator'
import {
    generateMerchantImage,
    generateMerchantCloudinaryId,
} from '../../utils/merchantImageGenerator'
import MerchantCard from '../Cards/MerchantCard'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { MuiThemeProvider } from '@material-ui/core'
import Radio from '@material-ui/core/Radio'
import baseSiteTheme from '../Base/baseSiteTheme'
import SortByComponent from './SortBy'
import { makeStyles } from '@material-ui/core/styles'
import { useLocation } from 'react-router-dom'
import CustomPagination from './CustomPagination'

const useStyles = makeStyles((theme) => ({
    featuredPromosTitle: {
        fontSize: '1.6rem',
        fontWeight: 'bold',
        textAlign: 'center',
        [theme.breakpoints.up('sm')]: {
            textAlign: 'left',
        },
    },
    featuredPromosRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: 30,
        margin: '1rem 0',
        alignItems: 'center',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            marginBottom: 20,
        },
    },
    featuredPromoTitle: {
        margin: 0,
        padding: 0,
        textAlign: 'center',
        fontWeight: 'bold',
        textDecoration: 'none',
        color: 'black',
    },
    featuredPromoLink: {
        textAlign: 'center',
        fontWeight: 'bold',
        textDecoration: 'underline',
        height: '3rem',
    },
    featuredPromo: {
        position: 'relative',
        cursor: 'pointer',
    },
    featuredPromoBadge: {
        color: '#592163',
        background: '#FEB511',
        fontWeight: 'bold',
        position: 'absolute',
        top: 0,
        left: 0,
        padding: '.5rem .55rem',
        borderTopLeftRadius: 5,
        borderBottomRightRadius: 5,
    },
}))

const getSearchState = (searchRef) => {
    return searchRef?.current?.state?.contextValue?.store?.getState() ?? null
}

const SortBy = connectSortBy(SortByComponent)

const SearchBox = ({ currentRefinement, refine }) => (
    <>
        <label htmlFor="buy-page-filters-search" className="scr-only">
            Search by brand or store name
        </label>
        <i className="icon icon-search buy-page-search-icon" />
        <input
            id="buy-page-filters-search"
            name="buy-page-filters-search"
            className="buy-page-filters-search browser-default"
            value={currentRefinement}
            type="search"
            onChange={(event) => refine(event.currentTarget.value)}
            placeholder="Search by brand or store name"
        />
    </>
)

const CustomSearchBox = connectSearchBox(SearchBox)

const CardTypeMenu = ({
    items,
    refine,
    currentRefinement,
    isEgiftsPage,
    isMobile,
    searchRef,
}) => {
    if (isMobile) {
        return null
    }

    const isSearching = getSearchState(searchRef)?.searching ?? true

    if (!isSearching && (items.length < 2 || isEgiftsPage)) {
        return null
    }

    const handleFilterChange = (event) => {
        if (event.target.value === 'all') {
            refine(null)
        } else {
            refine(event.target.value)
        }
    }

    items.sort((a, b) =>
        a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
    )

    return (
        <div
            className={`buy-page-card-type-filter${
                isMobile ? ' no-margin-top' : ''
            }`}
        >
            {isMobile ? (
                <span className="buy-page-card-type-filter__mobile-header upper accent-color">
                    Filter
                </span>
            ) : (
                <h3 className="uppercase-header ">Filter</h3>
            )}

            {items.length >= 1 && (
                <div>
                    <FormControlLabel
                        value="all"
                        control={
                            <Radio
                                color="primary"
                                onChange={handleFilterChange}
                                checked={currentRefinement === null}
                            />
                        }
                        label="View all"
                        labelPlacement="end"
                    />
                </div>
            )}

            {items.map((item) => {
                const fieldLabel =
                    item.label === 'egift'
                        ? 'eGift cards'
                        : 'Plastic gift cards'

                return (
                    <div key={item.label}>
                        <FormControlLabel
                            key={item.label}
                            value={item.label}
                            control={
                                <Radio
                                    color="primary"
                                    onChange={handleFilterChange}
                                    checked={currentRefinement === item.label}
                                />
                            }
                            label={fieldLabel}
                            labelPlacement="end"
                        />
                    </div>
                )
            })}
        </div>
    )
}

const CustomCardTypeMenu = connectMenu(CardTypeMenu)

const Hits = (props) => {
    const { hits, topPremiumMerchants, isEgiftsPage, searchRef } = props
    const searchState = getSearchState(searchRef)
    const currentQuery = searchState?.results?.query
    const isSearching = searchState?.searching ?? true
    const [firstHitsRetrieved, setFirstHitsRetrieved] = useState(false)
    const [impressionsSentToGA, setImpressionsSentToGA] = useState(false)

    useEffect(() => {
        if (!firstHitsRetrieved && !isSearching) {
            setFirstHitsRetrieved(true)
        }
    }, [firstHitsRetrieved, isSearching])

    useEffect(() => {
        if (!impressionsSentToGA && firstHitsRetrieved) {
            sendProductImpressionsToDatalayer(
                hits,
                window.datalayerImpressionListName
            )
            setImpressionsSentToGA(true)
        }
    }, [impressionsSentToGA, firstHitsRetrieved, hits])

    useEffect(() => {
        if (!isSearching && hits.length > 0 && currentQuery) {
            sendProductImpressionsToDatalayer(
                hits,
                window.datalayerImpressionListName
            )
        }
    }, [isSearching, hits, currentQuery])

    const mapHits = (hitObjects, topMerchantsObject) => {
        let displayedObjects = hitObjects
        if (firstHitsRetrieved && !hitObjects.length) {
            displayedObjects = topMerchantsObject
        }

        const hideVisaMCFromSearch = Boolean(window.React?.hideVisaMCFromSearch)
        if (hideVisaMCFromSearch) {
            displayedObjects = displayedObjects.filter((object) => {
                return (
                    object.name.toLowerCase() !== 'mastercard' &&
                    object.name.toLowerCase() !== 'visa'
                )
            })
        }

        // Sort Mastercard to the top, then sort Visa to the top so it's first.
        // Changes requested in GRANNY-3520/ GRANNY-3567
        displayedObjects.sort((a, b) => {
            if (
                a.name.toLowerCase() === 'mastercard' &&
                b.name.toLowerCase() === 'visa'
            ) {
                return 1
            }
            if (a.name.toLowerCase() === 'visa') {
                return -1
            }
            if (a.name.toLowerCase() === 'mastercard') {
                return -1
            }
            return 1
        })

        return displayedObjects.map((merchant, key) => {
            let subtitle = generateMerchantSubtitle(merchant)
            let merchantUrl = generateMerchantUrl(
                window.gcgBaseUrl,
                merchant,
                isEgiftsPage,
                false
            )

            return (
                <div
                    key={key}
                    className="col s12 m4 l3 transforming-image-column"
                >
                    {/* todo find out how to pass down variables to this component instead of referencing window */}
                    <a
                        className="merchant-card"
                        href={merchantUrl}
                        onClick={() => {
                            sendProductClickToDataLayer(
                                merchant.name,
                                merchant.__position,
                                gcgPointsToCashBack(
                                    merchant.max_possible_points
                                ),
                                merchant.max_discount_percentage,
                                window.datalayerImpressionListName
                            )
                        }}
                    >
                        <span className="card-image">
                            <span className="card-image__inner">
                                <MerchantCard
                                    title={`${merchant.name} Gift Card`}
                                    cloudinaryId={generateMerchantCloudinaryId(
                                        merchant,
                                        isEgiftsPage
                                    )}
                                    imageSrc={generateMerchantImage(
                                        merchant,
                                        isEgiftsPage
                                    )}
                                    lazyLoad={true}
                                />
                            </span>
                        </span>
                        <span className="card-image-aside">
                            <span className="card-title">
                                <div className="merchant-card__name-wrapper merchant-card__name-wrapper--one-line">
                                    <span className="merchant-card__merchant-name">{`${merchant.name} Gift Card`}</span>
                                </div>
                                <span className="card-title__subtitle">
                                    {subtitle}
                                </span>
                            </span>
                        </span>
                    </a>
                </div>
            )
        })
    }

    return (
        <div className={'row relative'} style={{ minHeight: '50vh' }}>
            {firstHitsRetrieved && hits.length === 0 && (
                <>
                    <div className="col s12 relative">
                        <h6>No Results Found for '{currentQuery}'</h6>
                        <p>Here's the top 12 brands we'd recommend instead</p>
                    </div>
                </>
            )}
            {mapHits(hits, topPremiumMerchants)}
        </div>
    )
}
const CustomHits = connectHits(Hits)

const ClearRefinementsButton = ({ items, refine, searchRef }) => {
    const currentQuery = getSearchState(searchRef)?.results?.query
    const hasQuery = currentQuery && currentQuery.length

    return (
        <button
            className={`btn btn-outline btn-rounded buy-page-clear-filters${
                !items.length && !hasQuery ? ' hide' : ' primary-color'
            }`}
            onClick={() => {
                if (!items.length) {
                    refine()
                } else {
                    refine(items)
                }
            }}
            disabled={!items.length && !hasQuery}
        >
            Clear filters
        </button>
    )
}

const CustomClearRefinements = connectCurrentRefinements(ClearRefinementsButton)

const CustomMenuStartingLetter = ({ items, currentRefinement, refine }) => {
    return (
        <div
            className={`row alpha-filters hide-on-small-and-down`}
            data-letter=""
        >
            <div
                className="input-field col s12"
                style={{ marginTop: '1rem', marginBottom: '1rem' }}
            >
                <ul className="bold no-margin">
                    {items
                        .sort((a, b) =>
                            a.label.toLowerCase() > b.label.toLowerCase()
                                ? 1
                                : -1
                        )
                        .map((item, key) => (
                            <li key={key}>
                                <a
                                    className={
                                        'primary-color' +
                                        (currentRefinement === item.label
                                            ? ' active'
                                            : '')
                                    }
                                    href={
                                        item.label === '#'
                                            ? '#1-9'
                                            : `#${item.label}`
                                    }
                                    data-layer-push-event="AlphabetSelector|Buy - Page Clicks|View As - Letter|1-9"
                                    onClick={(event) => {
                                        event.preventDefault()
                                        refine(item.value)
                                    }}
                                >
                                    {item.label}
                                </a>
                            </li>
                        ))}
                </ul>
            </div>
        </div>
    )
}

const featuredPromoMerchants = [
    {
        id: 0,
        img: 'https://res.cloudinary.com/gift-card-granny/image/upload/c_scale,f_auto,w_237/v1/GCG/merchants/home-chef-gift-card',
        discountBadge: '$100 gift card for $85',
        title: 'Home Chef',
        promoCode: 'HOMECHEF23',
        link: `${window.gcgBaseUrl}/buy-gift-cards/home-chef/?promo=HOMECHEF23`,
    },
    {
        id: 1,
        img: 'https://res.cloudinary.com/gift-card-granny/image/upload/c_scale,f_auto,w_237/v1/GCG/merchants/shutterfly-gift-card',
        discountBadge: '15% off',
        title: 'Shutterfly',
        promoCode: 'PHOTO23',
        link: `${window.gcgBaseUrl}/buy-gift-cards/shutterfly/?promo=PHOTO23`,
    },
    {
        id: 2,
        img: 'https://res.cloudinary.com/gift-card-granny/image/upload/c_scale,f_auto,w_237/v1/GCG/merchants/regal-gift-card',
        discountBadge: '$25 gift card for $20 ',
        title: 'Regal',
        promoCode: 'MOVIES2023',
        link: `${window.gcgBaseUrl}/buy-gift-cards/regal-cinemas/?promo=MOVIES2023`,
    },
    {
        id: 3,
        img: 'https://res.cloudinary.com/gift-card-granny/image/upload/c_scale,f_auto,w_237/v1/GCG/merchants/darden-restaurants-gift-card',
        discountBadge: '5% off',
        title: 'Darden Restaurants',
        promoCode: 'DARDEN5',
        link: `${window.gcgBaseUrl}/buy-gift-cards/darden-restaurants/?promo=DARDEN5`,
    },
]

const CustomMenuStartingLetterMenu = connectMenu(CustomMenuStartingLetter)

const AlgoliaSearch = () => {
    const sideNavCategories = window.React.sideNavCategories
    const sidebarLinks = window.React.sidebarLinks
    const marketplaceProtection = window.React.marketplaceProtection

    const searchClient = algoliasearch(
        window.React.algolia_application_id,
        window.React.algolia_search_api_key
    )
    const algoliaMerchantIndex = window.React.algolia_merchant_index

    const isEgiftsPage = window.React.isEgiftsPage ?? 0
    const categoryName = window.React.categoryName
    const relatedCategoriesSection = window.React.relatedCategoriesSection
    const defaultRefinement = window.React.default_refinement

    const topPremiumMerchants = JSON.parse(
        unescape(window.React.topPremiumMerchants)
    )
    const searchRef = useRef()

    const classes = useStyles()

    const location = useLocation()

    const [promoCodeActive, setPromoCodeActive] = useState(false)
    const [refinementByUrl, setRefinementByUrl] = useState('popular')

    const pageSpecificFilters = () => {
        if (isEgiftsPage) {
            return 'local:false AND enabled:true AND types:egift'
        }
        if (categoryName) {
            return `local:false AND enabled:true AND categories:'${categoryName}'`
        }
        return 'local:false AND enabled:true'
    }

    const renderRelatedCategories = () => {
        if (!categoryName) {
            return null
        }
        return (
            <React.Fragment>
                <div
                    dangerouslySetInnerHTML={{
                        __html: unescape(relatedCategoriesSection),
                    }}
                />
            </React.Fragment>
        )
    }

    // used for conditionally rendering one filter or the other
    const viewportWidth = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
    )

    useEffect(() => {
        const currentDate = new Date(
            Date.UTC(
                new Date().getUTCFullYear(),
                new Date().getUTCMonth(),
                new Date().getUTCDate(),
                new Date().getUTCHours(),
                new Date().getUTCMinutes(),
                new Date().getUTCSeconds()
            )
        )

        const endDate = new Date(Date.UTC(2024, 0, 1, 4, 59, 59))

        if (currentDate < endDate) {
            setPromoCodeActive(true)
        }
    }, [])

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const filterParam = urlParams.get('filter')

        const allowedFilters = ['popular', 'name', 'cashback']

        if (filterParam && allowedFilters.includes(filterParam)) {
            setRefinementByUrl(filterParam)
        }
    }, [])

    return (
        <MuiThemeProvider theme={baseSiteTheme}>
            <InstantSearch
                ref={searchRef}
                searchClient={searchClient}
                indexName={algoliaMerchantIndex}
                onSearchStateChange={(searchState) => {
                    // send impressions to Google Analytics if it's not a direct search
                    if (!searchState.query) {
                        const hits = getSearchState(searchRef)?.results?.hits
                        sendProductImpressionsToDatalayer(
                            hits,
                            window.datalayerImpressionListName
                        )
                    }
                }}
            >
                <Configure hitsPerPage={100} filters={pageSpecificFilters()} />
                <React.Fragment>
                    <div
                        id="gcg-side-nav"
                        className="hide-on-med-and-down gcg-side-nav"
                    >
                        <div
                            dangerouslySetInnerHTML={{
                                __html: unescape(sideNavCategories),
                            }}
                        />
                        {false &&
                            viewportWidth > 992 && ( // hiding filters (false &&) until we start selling CL cards again
                                <CustomCardTypeMenu
                                    attribute="types"
                                    isEgiftsPage={isEgiftsPage}
                                    isMobile={false}
                                    searchRef={searchRef}
                                />
                            )}

                        <div
                            dangerouslySetInnerHTML={{
                                __html: unescape(sidebarLinks),
                            }}
                        />
                        <div
                            dangerouslySetInnerHTML={{
                                __html: unescape(marketplaceProtection),
                            }}
                        />
                    </div>

                    <div
                        id="gcg-right-side"
                        className="col s12 l10 no-sidebar-medium-down-full-width-mobile no-border"
                    >
                        {promoCodeActive &&
                            location?.pathname?.startsWith(
                                '/e-gift-cards-discounts'
                            ) &&
                            featuredPromoMerchants.length > 0 && (
                                <>
                                    <div
                                        className={classes.featuredPromosTitle}
                                    >
                                        Featured Promotions
                                    </div>
                                    <div className={classes.featuredPromosRow}>
                                        {featuredPromoMerchants.map(
                                            (featuredPromo) => {
                                                return (
                                                    <div key={featuredPromo.id}>
                                                        <a
                                                            href={
                                                                featuredPromo.link
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    classes.featuredPromo
                                                                }
                                                            >
                                                                <div
                                                                    className={
                                                                        classes.featuredPromoBadge
                                                                    }
                                                                >
                                                                    {
                                                                        featuredPromo.discountBadge
                                                                    }
                                                                </div>
                                                                <img
                                                                    className={
                                                                        classes.featuredPromoImg
                                                                    }
                                                                    width={216}
                                                                    height={137}
                                                                    src={
                                                                        featuredPromo.img
                                                                    }
                                                                    alt={`${featuredPromo.title} card image`}
                                                                />

                                                                <p
                                                                    className={
                                                                        classes.featuredPromoTitle
                                                                    }
                                                                >
                                                                    {
                                                                        featuredPromo.title
                                                                    }
                                                                </p>
                                                                <div
                                                                    className={
                                                                        classes.featuredPromoLink
                                                                    }
                                                                >
                                                                    <div>
                                                                        PROMOCODE:{' '}
                                                                    </div>
                                                                    <div>
                                                                        {
                                                                            featuredPromo.promoCode
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </div>
                                                )
                                            }
                                        )}
                                    </div>
                                </>
                            )}

                        <div className="row buy-page-filters buy-page-filters--with-clear-refinements no-padding-mobile relative">
                            <CustomClearRefinements
                                clearsQuery={true}
                                searchRef={searchRef}
                            />
                            <div className="col s12 m8 hide-on-small-and-down relative ">
                                <CustomSearchBox
                                    defaultRefinement={defaultRefinement}
                                />
                            </div>
                            <div className="col s12 m4 margin-bottom-10-small-and-down">
                                <SortBy
                                    items={[
                                        {
                                            value: `${algoliaMerchantIndex}_popular`,
                                            label: 'Sort by: Most Popular',
                                        },
                                        {
                                            value: `${algoliaMerchantIndex}_cashback`,
                                            label: 'Sort by: Highest Cash Back',
                                        },
                                        {
                                            value: `${algoliaMerchantIndex}_name`,
                                            label: 'Sort by: Brand Name',
                                        },
                                    ]}
                                    defaultRefinement={`${algoliaMerchantIndex}_${refinementByUrl}`}
                                />
                            </div>
                            <ScrollTo>
                                <CustomMenuStartingLetterMenu
                                    attribute="name_first_letter"
                                    limit={27}
                                />
                            </ScrollTo>
                            {viewportWidth <= 992 && (
                                <CustomCardTypeMenu
                                    attribute="types"
                                    isEgiftsPage={isEgiftsPage}
                                    isMobile={true}
                                    searchRef={searchRef}
                                />
                            )}
                            <CustomHits
                                topPremiumMerchants={topPremiumMerchants}
                                isEgiftsPage={isEgiftsPage}
                                searchRef={searchRef}
                            />
                            <CustomPagination />
                        </div>
                        {renderRelatedCategories()}
                    </div>
                </React.Fragment>
            </InstantSearch>
        </MuiThemeProvider>
    )
}

export default AlgoliaSearch
